<template>
	<div>
		<div class="tw-text-3xl tw-font-bold" v-if="!isDone">
			{{ $t('integrations.import-noon-products-title') }}
		</div>
		<div class="tw-flex tw-items-center tw-gap-2" v-else>
			<div class="tw-text-3xl tw-font-bold">
				{{ $t('integrations.products-imported') }}
			</div>
			<div
				class="'tw-border-secondary-500 tw-z-10 tw-flex tw-items-center tw-justify-center tw-rounded-full tw-border tw-bg-secondary-500 tw-text-white"
				style="width: 32px; height: 32px"
			>
				<AppIcon name="check" />
			</div>
		</div>
		<div class="tw-font-medium tw-text-gray-500" v-if="!isDone">
			{{ $t('integrations.import-noon-products-description') }}
		</div>
		<div class="tw-font-medium tw-text-gray-500" v-else>
			{{ $t('integrations.products-imported-description') }}
		</div>
		<AppForm
			@submit="onSubmit"
			class="tw-mt-3 tw-flex tw-flex-col tw-gap-4"
			v-if="!isDone && !isFinished"
			style="width: 70%"
		>
			<div class="tw-flex tw-flex-col tw-gap-1">
				<AppFileUpload
					v-model="form.file"
					placeholder="LiveNoon.xlsx"
					:label="$t('integrations.upload-live-products')"
					:rules="['required']"
					acceptTypes=".csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
					:allowedExts="['xlsx', 'csv']"
					:disabled="loading"
					uploadType="s3"
					shortPath
				>
					<template #hint>
						<div
							class="tw-cursor-pointer tw-text-gray-600 tw-underline"
							@click="openHelpDialog('live')"
						>
							{{ $t('integrations.how-to-export-my-live-catalog') }}
						</div>
					</template>
				</AppFileUpload>
				<AppFileUpload
					v-model="form.not_live_file"
					placeholder="NotLiveNoon.xlsx"
					:label="$t('integrations.upload-not-live-products')"
					:rules="['required']"
					acceptTypes=".csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
					:allowedExts="['xlsx', 'csv']"
					:disabled="loading"
					uploadType="s3"
					shortPath
				>
					<template #hint>
						<div
							class="tw-cursor-pointer tw-text-gray-600 tw-underline"
							@click="openHelpDialog('not-live')"
						>
							{{ $t('integrations.how-to-export-my-not-live-catalog') }}
						</div>
					</template>
				</AppFileUpload>
				<AppFileUpload
					v-model="form.products_quantity_file"
					placeholder="Quantities.xlsx"
					:label="$t('integrations.upload-quantities')"
					:rules="['required']"
					acceptTypes=".csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
					:allowedExts="['xlsx', 'csv']"
					:disabled="loading"
					uploadType="s3"
					shortPath
				>
					<!-- <template #hint>
						<div
							class="tw-cursor-pointer tw-text-gray-600 tw-underline"
							@click="openHelpDialog('quantities')"
						>
							{{ $t('integrations.how-to-export-my-stock-quantity') }}
						</div>
					</template> -->
				</AppFileUpload>
			</div>

			<div>
				<AppButton
					:label="
						loading ? $t('integrations.uploading-files') : $t('integrations.upload-files')
					"
					size="xl"
					class="tw-rounded-full"
					endIcon="assets/v2/image/icons/upload.svg"
					type="submit"
					style="width: 250px"
					:disabled="loading"
				/>
			</div>
		</AppForm>
		<div class="tw-flex tw-items-center tw-gap-3" v-else>
			<AppButton
				:label="$t('integrations.re-import-products')"
				size="xl"
				class="tw-rounded-full"
				type="button"
				secondary
				variant="gray"
				endIcon="assets/v2/image/icons/upload.svg"
				:disabled="loading || isFinished"
				@click="reImport"
			/>
			<AppButton
				:label="$t('integrations.complete-setup')"
				size="xl"
				class="tw-rounded-full"
				type="button"
				endIcon="assets/v2/image/icons/check.svg"
				@click="nextStep"
				:disabled="nextStepLoading || isFinished"
			/>
		</div>
		<HelpDialogExportCatalog ref="helpDialogExportCatalogRef" />
	</div>
</template>

<script setup>
import { useLinkMarketplaceStore } from '../../../../stores/link-marketplace';
import { ref, computed } from 'vue';
import { marketplaceStepStatusEnum } from '../../helper';
import { Marketplace } from '../../../../entities/marketplace';
import HelpDialogExportCatalog from './help-dialog-export-catalog.vue';
import { useRouter } from 'vue-router';

const linkMarketplaceStore = useLinkMarketplaceStore();

const router = useRouter();

const loading = ref(
	linkMarketplaceStore.currentStepData.status === marketplaceStepStatusEnum.InProgress,
);
const isDone = ref(
	linkMarketplaceStore.currentStepData.status === marketplaceStepStatusEnum.Complete,
);
const nextStepLoading = ref(false);

const form = ref({
	file: '',
	not_live_file: '',
	products_quantity_file: '',
	multi_files: true,
});

const helpDialogExportCatalogRef = ref(null);

const isFinished = computed(() => {
	return linkMarketplaceStore.currentStepData.status === marketplaceStepStatusEnum.Finished;
});

const onSubmit = async () => {
	loading.value = true;
	try {
		await Marketplace.importNoonFiles({
			...form.value,
			linked_marketplace_id: linkMarketplaceStore.linkedMarketplaceId,
		});
		await linkMarketplaceStore.refetchSteps();
	} finally {
		loading.value = false;
		isDone.value = true;
	}
};

const reImport = async () => {
	loading.value = false;
	isDone.value = false;
};

const nextStep = async () => {
	nextStepLoading.value = true;
	try {
		await Marketplace.finishWizardStep(linkMarketplaceStore.currentStepData.id);
		await linkMarketplaceStore.refetchSteps();
		isDone.value = true;
		router.push('/v2/integrations/marketplaces');
	} finally {
		nextStepLoading.value = false;
	}
};

const openHelpDialog = (dialogName) => {
	helpDialogExportCatalogRef.value.open(dialogName === 'not-live' ? false : true);
};
</script>

<style lang="scss" scoped></style>
